import { type AllStatuses } from '@opallabs/concepts'
import { type icons } from '@opallabs/constellation'
import { Size } from '@opallabs/gem'
import { Avatar, Icon, WorkflowIcon } from '@opallabs/lapidary'
import {
  CalendarFilterCategoryIds,
  calendarStatusOptionId,
} from '../categories'
import { CALENDAR_CONTENT_STATUS_MARKS } from '../statusMarks'
import {
  type CalendarFilterCategory,
  type CalendarFilterOption,
} from '../types'

/** The 16px mark the design puts in front of a row, and on a token. */
const MARK_SIZE = Size.Icon

/**
 * The workflow mark each word of the status list has earned, by the id the word
 * is offered under.
 *
 * These are the marks the grid already puts on a chip and the week view puts on
 * a card, so a row in the drawer and the item it will keep wear the same shape.
 * The content half reads them from `CALENDAR_CONTENT_STATUS_MARKS`, which the
 * grid reads too, so the two can never drift; the moment half is summarised
 * from the workflow over a moment and is nobody else's business, so it is
 * written out here.
 */
const WORKFLOW_MARKS: Readonly<Record<string, AllStatuses>> = {
  ...Object.fromEntries(
    Object.entries(CALENDAR_CONTENT_STATUS_MARKS).map(([status, mark]) => [
      calendarStatusOptionId('content', status),
      mark,
    ])
  ),
  'moment.approved': 'approval-approved',
  'moment.declined': 'approval-denied',
  'moment.not_started': 'not-started',
  'moment.paused': 'paused',
  'moment.pending_approval': 'approval-pending',
  'moment.pending_tasks': 'tasks-pending',
  'moment.tasks_complete': 'tasks-complete',
}

/**
 * The rows the workflow icon set has no mark for.
 *
 * Idea is a moment with nothing under it rather than a state of work, so it
 * wears the outlined moment glyph the grid and the week view already use to
 * mean "a moment".
 */
const GLYPHS: Readonly<Record<string, Record<string, keyof typeof icons.gem>>> =
  {
    [CalendarFilterCategoryIds.privacy]: {
      private: 'gem-locked',
      public: 'gem-globe',
    },
    [CalendarFilterCategoryIds.status]: {
      'moment.idea': 'gem-moment-outlined',
    },
  }

/**
 * The mark every option of a category wears alike: a board is a board, and it
 * wears the glyph the navigation gives boards.
 */
const CATEGORY_GLYPHS: Readonly<Record<string, keyof typeof icons.gem>> = {
  [CalendarFilterCategoryIds.board]: 'gem-grid',
}

/** An option offered under a channel, which is what an account is. */
const isAccount = (
  category: CalendarFilterCategory,
  option: CalendarFilterOption
): boolean =>
  category.id === CalendarFilterCategoryIds.channels &&
  option.group !== undefined &&
  option.group.id !== option.id

/**
 * The mark a built-in option wears, wherever the drawer draws that option: on
 * its row and on its token.
 *
 * Only the surface can draw a channel's artwork, so it keeps its own
 * `renderOptionIcon` and this answers for whatever that leaves unmarked. An
 * account wears the avatar the grid gives it when content is grouped by
 * account: its initials, since the filter options carry no picture. A row
 * that stands for other rows — a half of the status list — carries no mark: it
 * is named by its heading, and the marks belong to the words under it.
 */
export const builtInOptionIcon = (
  category: CalendarFilterCategory,
  option: CalendarFilterOption
): React.ReactNode => {
  if (isAccount(category, option))
    return <Avatar fullName={option.label} size={MARK_SIZE} />

  const glyph = GLYPHS[category.id]?.[option.id] ?? CATEGORY_GLYPHS[category.id]
  if (glyph !== undefined)
    return <Icon ariaHidden={true} glyph={glyph} size={MARK_SIZE} />

  const status =
    category.id === CalendarFilterCategoryIds.status
      ? WORKFLOW_MARKS[option.id]
      : undefined

  return status === undefined ? null : (
    <WorkflowIcon size={MARK_SIZE} status={status} />
  )
}
